<template>
  <div class="image-widget" :style="rootStyles" :class="widgetClasses">
    <div class="image-container" :style="containerStyles" @click="handleImageClick">
      <a 
        v-if="widget.linkUrl" 
        :href="widget.linkUrl" 
        target="_blank"
        class="image-link"
        @click.stop
      >
        <img 
          :src="imageUrl"
          :alt="widget.altText || 'Image'"
          :style="imageStyles"
          :class="['widget-image', hoverClass]"
          :width="intrinsicWidth || undefined"
          :height="intrinsicHeight || undefined"
          @error="handleImageError"
          :loading="isPriority ? 'eager' : 'lazy'"
          :fetchpriority="isPriority ? 'high' : 'auto'"
          :decoding="isPriority ? 'sync' : 'async'"
        />
        <div v-if="widget.caption" class="image-caption">{{ widget.caption }}</div>
      </a>
      <div v-else>
        <img 
          :src="imageUrl"
          :alt="widget.altText || 'Image'"
          :style="imageStyles"
          :class="['widget-image', hoverClass, { 'cursor-zoom': widget.enableLightbox }]"
          :width="intrinsicWidth || undefined"
          :height="intrinsicHeight || undefined"
          @error="handleImageError"
          :loading="isPriority ? 'eager' : 'lazy'"
          :fetchpriority="isPriority ? 'high' : 'auto'"
          :decoding="isPriority ? 'sync' : 'async'"
        />
        <div v-if="widget.caption" class="image-caption">{{ widget.caption }}</div>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <Teleport to="body">
      <div v-if="showLightbox" class="lightbox-overlay" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <button class="lightbox-close" @click="closeLightbox" aria-label="Close">
            <i class="bi bi-x-lg"></i>
          </button>
          <img :src="rawImageUrl" :alt="widget.altText || 'Image'" class="lightbox-image" />
          <div v-if="widget.caption" class="lightbox-caption">{{ widget.caption }}</div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import type { ImageWidget } from '@/types/pagebuilder'
import { API_BASE_URL } from '@/utils/config'
import { UPLOADS_BASE_URL } from '@/config/env'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'
import { resolveImageWidgetWidth, normalizeImageWidth } from '@/utils/imageWidgetSize'
import { toStaticLcpUrl } from '@/utils/lcpImage'

interface Props {
  widget: ImageWidget
  priority?: boolean
}

const props = defineProps<Props>()

/**
 * Home (and similar) can provide a one-shot claim so the first mounted
 * image widget is eager + high priority — even if nested in columns or
 * not widgetIndex 0. Fixes Lighthouse "LCP image was lazily loaded".
 */
const claimLcpImagePriority = inject<(() => boolean) | null>('claimLcpImagePriority', null)
const lcpClaimedLocally = ref(false)
if (!props.priority && claimLcpImagePriority) {
  lcpClaimedLocally.value = claimLcpImagePriority()
}

const isPriority = computed(() => !!props.priority || lcpClaimedLocally.value)

const showLightbox = ref(false)
const useFullResolution = ref(false)

const handleImageClick = () => {
  if (props.widget.enableLightbox && !props.widget.linkUrl) {
    showLightbox.value = true
  }
}

const closeLightbox = () => {
  showLightbox.value = false
}

const rawImageUrl = computed(() => {
  if (!props.widget.imageUrl) return `${API_BASE_URL}/placeholder.php?width=600&height=400&text=Click to add image&bg=e9ecef&color=6c757d`
  
  // Skip blob URLs as they are temporary browser-generated URLs
  if (props.widget.imageUrl.startsWith('blob:')) {
    return props.widget.imageUrl // Keep blob for preview
  }
  
  // Handle full HTTP URLs
  if (props.widget.imageUrl.startsWith('http')) return props.widget.imageUrl
  
  // Check if the image path already includes a subfolder
  if (props.widget.imageUrl.includes('/')) {
    return `${UPLOADS_BASE_URL}/${props.widget.imageUrl}`
  }
  
  // Bare filename (gallery) → imgs/images; matches EnhancedTextWidget / EnhancedImagePicker
  return `${UPLOADS_BASE_URL}/imgs/images/${props.widget.imageUrl}`
})

/** Priority/LCP images use resized -lcp-800.webp (same URL as index.php preload) */
const imageUrl = computed(() => {
  const raw = rawImageUrl.value
  if (!isPriority.value || useFullResolution.value) return raw
  return toStaticLcpUrl(raw, 800)
})

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  if (isPriority.value && !useFullResolution.value && img.src.includes('-lcp-')) {
    useFullResolution.value = true
    return
  }
  img.src = `${API_BASE_URL}/placeholder.php?width=600&height=400&text=Image Error&bg=f8d7da&color=721c24`
}

const imageWidth = computed(() =>
  resolveImageWidgetWidth(props.widget.sizePreset, props.widget.width),
)

const resolvedImageWidth = computed(() => normalizeImageWidth(imageWidth.value))

/** Explicit width/height attrs for CLS (Lighthouse "Media element lacking an explicit size") */
const intrinsicWidth = computed(() => {
  const w = resolvedImageWidth.value
  if (typeof w === 'number' && w > 0) return Math.round(w)
  if (typeof w === 'string') {
    const m = w.match(/^(\d+(?:\.\d+)?)px$/)
    if (m) return Math.round(parseFloat(m[1]))
  }
  // Widget often styles width:600px via CSS; default matches common editor size
  return 600
})

const intrinsicHeight = computed(() => {
  const h = props.widget.height
  if (typeof h === 'number' && h > 0) return Math.round(h)
  if (typeof h === 'string') {
    const m = h.match(/^(\d+(?:\.\d+)?)px$/)
    if (m) return Math.round(parseFloat(m[1]))
  }
  // 3:2 fallback — reserves space before decode
  return Math.round(intrinsicWidth.value * (2 / 3))
})

const hasFrameBackground = computed(() => {
  const widget = props.widget
  if (widget.backgroundFillMode === 'gradient') return true
  return Boolean(widget.backgroundColor && widget.backgroundColor !== 'transparent')
})

const containerStyles = computed(() => {
  const styles: Record<string, string> = {
    ...resolveWidgetBackgroundStyle(props.widget),
    display: 'inline-block',
    maxWidth: '100%',
    boxSizing: 'border-box',
    verticalAlign: 'top',
  }

  if (props.widget.padding) {
    styles.padding = props.widget.padding
  } else {
    if (props.widget.paddingTop) styles.paddingTop = props.widget.paddingTop
    if (props.widget.paddingRight) styles.paddingRight = props.widget.paddingRight
    if (props.widget.paddingBottom) styles.paddingBottom = props.widget.paddingBottom
    if (props.widget.paddingLeft) styles.paddingLeft = props.widget.paddingLeft
  }

  if (hasFrameBackground.value && !styles.padding && !props.widget.paddingTop) {
    styles.padding = '0.75rem'
  }

  if (props.widget.borderStyle && props.widget.borderStyle !== 'none') {
    styles.borderStyle = props.widget.borderStyle
    if (props.widget.borderWidth) styles.borderWidth = props.widget.borderWidth
    if (props.widget.borderColor) styles.borderColor = props.widget.borderColor
  }

  if (props.widget.borderRadius) {
    styles.borderRadius = props.widget.borderRadius
    styles.overflow = 'hidden'
  }

  if (props.widget.boxShadow) {
    styles.boxShadow = props.widget.boxShadow
  } else if (props.widget.boxShadowHorizontal || props.widget.boxShadowVertical) {
    const h = props.widget.boxShadowHorizontal || '0px'
    const v = props.widget.boxShadowVertical || '0px'
    const b = props.widget.boxShadowBlur || '10px'
    const s = props.widget.boxShadowSpread || '0px'
    const c = props.widget.boxShadowColor || 'rgba(0,0,0,0.1)'
    const p = props.widget.boxShadowPosition === 'inset' ? 'inset ' : ''
    styles.boxShadow = `${p}${h} ${v} ${b} ${s} ${c}`
  }

  if (imageWidth.value === '100%') {
    styles.width = '100%'
  } else if (resolvedImageWidth.value !== 'auto') {
    // Cap size with maxWidth — never set fixed width (breaks mobile when inline 600px wins over CSS)
    styles.width = '100%'
    styles.maxWidth = resolvedImageWidth.value
  }

  return styles
})

const rootStyles = computed(() => {
  const styles: Record<string, string> = {
    textAlign: props.widget.alignment || 'center',
    width: '100%',
    maxWidth: '100%',
  }

  // Custom width belongs on the inner image-container, not the root.
  // A 150px root cannot be centered in a column via text-align.
  if (props.widget.maxWidth) styles.maxWidth = props.widget.maxWidth
  if (props.widget.margin) {
    styles.margin = props.widget.margin
  } else {
    if (props.widget.marginTop) styles.marginTop = props.widget.marginTop
    if (props.widget.marginRight) styles.marginRight = props.widget.marginRight
    if (props.widget.marginBottom) styles.marginBottom = props.widget.marginBottom
    if (props.widget.marginLeft) styles.marginLeft = props.widget.marginLeft
  }

  if (props.widget.entranceAnimation && props.widget.entranceAnimation !== 'none') {
    const duration = props.widget.animationDuration || '0.5s'
    const delay = props.widget.animationDelay || '0s'
    const iterations = props.widget.animationIterationCount || '1'
    styles.animation = `${props.widget.entranceAnimation} ${duration} ease-out ${delay} ${iterations} both`
  }

  return styles
})

const widgetClasses = computed(() => {
  const classes: string[] = []
  if (props.widget.customClass) classes.push(props.widget.customClass)
  if (props.widget.entranceAnimation && props.widget.entranceAnimation !== 'none') {
    classes.push('widget-animated')
  }
  if (props.widget.hidden_on_mobile) classes.push('d-none d-md-block')
  if (props.widget.hidden_on_tablet) classes.push('d-md-none d-lg-block')
  if (props.widget.hidden_on_desktop) classes.push('d-lg-none')
  return classes.join(' ')
})

const imageStyles = computed(() => {
  const width = resolvedImageWidth.value
  const styles: Record<string, string | number> = {
    width: width === 'auto' ? 'auto' : width,
    height: props.widget.height || 'auto',
    maxWidth: '100%',
    display: 'block',
    boxShadow: props.widget.shadow ? '0 4px 15px rgba(0,0,0,0.1)' : 'none',
    opacity: props.widget.opacity || 1,
    border: props.widget.border || 'none',
  }

  if (!props.widget.borderRadius) {
    styles.borderRadius = '0'
  }

  if (props.widget.objectFit && props.widget.objectFit !== 'none') {
    styles.objectFit = props.widget.objectFit
  }

  return styles
})

const hoverClass = computed(() => {
  const effect = props.widget.hoverEffect
  if (!effect || effect === 'none') return ''
  return `hover-${effect}`
})
</script>

<style scoped>
.image-widget {
  margin: 1rem 0;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.image-container {
  display: inline-block;
  position: relative;
  max-width: 100%;
}

.widget-image {
  display: inline-block;
  transition: all 0.3s ease;
}

.cursor-zoom {
  cursor: zoom-in;
}

.image-caption {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #6c757d;
  font-style: italic;
}

/* Hover Effects */
.hover-zoom:hover {
  transform: scale(1.05);
}

.hover-lift:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

.hover-brightness:hover {
  filter: brightness(1.2);
}

.hover-grayscale {
  filter: grayscale(100%);
  transition: filter 0.3s ease;
}

.hover-grayscale:hover {
  filter: grayscale(0%);
}

.hover-rotate:hover {
  transform: rotate(5deg) scale(1.05);
}

.hover-blur {
  transition: filter 0.3s ease;
}

.hover-blur:hover {
  filter: blur(2px);
}

/* Keep original hover for links */
.image-link:hover .widget-image:not([class*="hover-"]) {
  transform: scale(1.02);
}

.widget-image:hover:not([class*="hover-"]) {
  transform: scale(1.02);
}

/* Lightbox Styles */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  animation: zoomIn 0.3s ease;
}

@keyframes zoomIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.lightbox-image {
  max-width: 100%;
  max-height: 85vh;
  width: auto;
  height: auto;
  display: block;
  margin: 0 auto;
  box-shadow: 0 10px 50px rgba(0,0,0,0.5);
  border-radius: 4px;
}

.lightbox-caption {
  text-align: center;
  color: white;
  margin-top: 1rem;
  font-size: 1rem;
}

.lightbox-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.lightbox-close:hover {
  background: rgba(255,255,255,0.3);
  transform: rotate(90deg);
}

/* Responsive - never let fixed editor widths overflow the viewport */
@media (max-width: 768px) {
  .image-widget {
    max-width: 100% !important;
    width: 100% !important;
    overflow: hidden;
  }
  
  .image-container {
    display: block !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .widget-image {
    max-width: 100% !important;
    width: 100% !important;
    height: auto !important;
    object-fit: contain;
  }
  
  .image-link {
    display: block !important;
    max-width: 100% !important;
    width: 100% !important;
  }
  
  .image-link .widget-image {
    max-width: 100% !important;
    width: 100% !important;
  }

  .lightbox-overlay {
    padding: 1rem;
  }

  .lightbox-close {
    top: 10px;
    right: 10px;
  }
}

@media (max-width: 576px) {
  .widget-image {
    margin: 0.5rem 0;
  }
}
</style>